import {
  CUSTOMER_SERVICE_ORGANIZATIONS_RESOURCE,
  DEVICE_ENROLLMENT_TOKENS_RESOURCE,
  ResourceName,
  TELEPHONY_RESOURCE,
  TEST_CUSTOMERS_RESOURCE,
  WEB_ORDERS_RESOURCE,
} from "../constants/resources";
import { PortalAccessQuery, UserType } from "../generated/graphql";
import { PORTAL_ACCESS_QUERY } from "../graphql/queries";
import { client } from "../lib/apolloClient";
import { AuthSurface, getAuthData } from "../lib/authStorage";

// Permissions of the signed-in user's staff roles, as currentUserPermissions
// reports them. The backend keeps the role -> permission matrix and enforces
// every permission; the portal only hides what the user may not use.
export enum StaffPermission {
  CrmRead = "crm.read",
  CrmDuplicatesReview = "crm.duplicates_review",
  SubscriptionDashboardRead = "subscription_dashboard.read",
  SalesforceIntegrationManage = "salesforce_integration.manage",
  PlacetelIntegrationManage = "placetel_integration.manage",
  TelephonyUse = "telephony.use",
  OrganizationBillingManage = "organization_billing.manage",
  LearningGrantsManage = "learning_grants.manage",
  ManagerInvitationsManage = "manager_invitations.manage",
  DeviceEnrollmentManage = "device_enrollment.manage",
  TestCustomersCreate = "test_customers.create",
  AnalyticsRead = "analytics.read",
  StaffManage = "staff.manage",
  MarketingRead = "marketing.read",
  MarketingDeliveryManage = "marketing_delivery.manage",
  BillingRead = "billing.read",
  BillingNotificationsHandle = "billing_notifications.handle",
  DhlIntegrationManage = "dhl_integration.manage",
  LogisticsManage = "logistics.manage",
  LogisticsRead = "logistics.read",
  SalesforceReportsRead = "salesforce_reports.read",
}

export type PortalAccess = {
  surface: AuthSurface;
  // Organization managers reach their organization's data through their user
  // kind: the backend reports permissions for staff roles only.
  kind?: UserType;
  hasLearningContent: boolean;
  permissions: readonly string[];
};

// Every resource react-admin checks with canAccess: the menu, the routes and
// the list, show, edit and create pages, including embedded lists.
export type PortalResource =
  | ResourceName
  | typeof CUSTOMER_SERVICE_ORGANIZATIONS_RESOURCE
  | typeof TEST_CUSTOMERS_RESOURCE
  | typeof TELEPHONY_RESOURCE
  | typeof DEVICE_ENROLLMENT_TOKENS_RESOURCE
  | typeof WEB_ORDERS_RESOURCE
  | "PractitionerNotes"
  | "PractitionerActivities"
  | "PatientActivities";

// All active staff roles grant crm.read; this helper controls the sidebar and the start page only.
export const hasStaffNavigationAccess = (
  resourceAccess: Partial<Record<PortalResource, boolean>> | undefined
): boolean => resourceAccess?.[ResourceName.Crm] === true;

type AccessRule = (access: PortalAccess) => boolean;

const organizationManager: AccessRule = access => access.surface === "admin" && access.kind === UserType.Manager;

const anyPermission =
  (...permissions: StaffPermission[]): AccessRule =>
  access =>
    access.surface === "admin" && permissions.some(permission => access.permissions.includes(permission));

// E-learning is experimental: managers only reach it once their organization
// actually has learning content assigned.
const managerWithLearningContent: AccessRule = access => organizationManager(access) && access.hasLearningContent;

const RESOURCE_ACCESS: Record<PortalResource, AccessRule> = {
  [ResourceName.Practitioner]: organizationManager,
  [ResourceName.Patient]: organizationManager,
  [ResourceName.Location]: organizationManager,
  [ResourceName.Device]: organizationManager,
  [ResourceName.Procedure]: organizationManager,
  PractitionerNotes: organizationManager,
  PractitionerActivities: organizationManager,
  PatientActivities: organizationManager,
  [ResourceName.LearningAssignment]: managerWithLearningContent,
  [ResourceName.LearningProgress]: managerWithLearningContent,
  [ResourceName.Learning]: access => access.surface === "learning",
  [ResourceName.Analytics]: anyPermission(StaffPermission.AnalyticsRead),
  [ResourceName.Crm]: anyPermission(StaffPermission.CrmRead),
  [ResourceName.CrmDuplicateReview]: anyPermission(StaffPermission.CrmDuplicatesReview),
  [ResourceName.Marketing]: anyPermission(StaffPermission.MarketingRead),
  [WEB_ORDERS_RESOURCE]: anyPermission(StaffPermission.MarketingRead, StaffPermission.CrmRead),
  [ResourceName.SalesforceDashboard]: anyPermission(StaffPermission.SubscriptionDashboardRead),
  [ResourceName.SalesforceReports]: anyPermission(StaffPermission.SalesforceReportsRead),
  [ResourceName.SalesforceIntegration]: anyPermission(StaffPermission.SalesforceIntegrationManage),
  [ResourceName.MarketingDelivery]: anyPermission(StaffPermission.MarketingDeliveryManage),
  [ResourceName.FinanceNotifications]: anyPermission(StaffPermission.BillingRead),
  [ResourceName.FinanceNotificationHandling]: anyPermission(StaffPermission.BillingNotificationsHandle),
  // Finance and Admin staff get the badge: the permission that lets them dismiss.
  [ResourceName.FinanceNotificationBadge]: anyPermission(StaffPermission.BillingNotificationsHandle),
  [ResourceName.BillwerkCustomerMatch]: anyPermission(StaffPermission.BillingRead),
  [ResourceName.BillwerkPlanVariants]: anyPermission(StaffPermission.BillingRead),
  [ResourceName.FinanceOverview]: anyPermission(StaffPermission.BillingRead),
  [ResourceName.LogisticsReports]: anyPermission(StaffPermission.LogisticsRead, StaffPermission.SalesforceReportsRead),
  [ResourceName.LogisticsDhlSettings]: anyPermission(
    StaffPermission.LogisticsManage,
    StaffPermission.DhlIntegrationManage
  ),
  [ResourceName.LogisticsDhlShippingSettings]: anyPermission(StaffPermission.LogisticsManage),
  [ResourceName.LogisticsDhlAccountSettings]: anyPermission(StaffPermission.DhlIntegrationManage),
  [ResourceName.LogisticsInventory]: anyPermission(StaffPermission.LogisticsRead),
  [TEST_CUSTOMERS_RESOURCE]: anyPermission(StaffPermission.TestCustomersCreate),
  [TELEPHONY_RESOURCE]: anyPermission(StaffPermission.TelephonyUse),
  [ResourceName.Staff]: anyPermission(StaffPermission.StaffManage),
  [ResourceName.Integrations]: anyPermission(StaffPermission.PlacetelIntegrationManage),
  [ResourceName.Invitation]: anyPermission(StaffPermission.ManagerInvitationsManage),
  [ResourceName.LearningGrant]: anyPermission(StaffPermission.LearningGrantsManage),
  [ResourceName.OrganizationBilling]: anyPermission(StaffPermission.OrganizationBillingManage),
  // The facilities list leads to the billing and the learning grants tab.
  [CUSTOMER_SERVICE_ORGANIZATIONS_RESOURCE]: anyPermission(
    StaffPermission.OrganizationBillingManage,
    StaffPermission.LearningGrantsManage
  ),
  [DEVICE_ENROLLMENT_TOKENS_RESOURCE]: access =>
    organizationManager(access) || anyPermission(StaffPermission.DeviceEnrollmentManage)(access),
  // No role uses the generic organization list.
  [ResourceName.Organization]: () => false,
};

const isPortalResource = (resource: string): resource is PortalResource =>
  Object.prototype.hasOwnProperty.call(RESOURCE_ACCESS, resource);

export const canAccessResource = (access: PortalAccess, resource: string): boolean =>
  isPortalResource(resource) && RESOURCE_ACCESS[resource](access);

export const accessibleResources = (access: PortalAccess): PortalResource[] =>
  (Object.keys(RESOURCE_ACCESS) as PortalResource[]).filter(resource => RESOURCE_ACCESS[resource](access));

// Each access refresh reads current permissions. Concurrent checks share the
// auth provider request; Apollo must not keep grants for the whole session.
export const getPortalAccess = async (): Promise<PortalAccess | null> => {
  const auth = getAuthData();
  if (!auth) return null;
  if (auth.surface === "learning") return { surface: "learning", hasLearningContent: false, permissions: [] };

  const { data } = await client.query<PortalAccessQuery>({ query: PORTAL_ACCESS_QUERY, fetchPolicy: "network-only" });
  if (!data.me) throw new Error("User not found");
  return {
    surface: "admin",
    kind: data.me.kind,
    hasLearningContent: data.hasLearningContent,
    permissions: data.currentUserPermissions,
  };
};
